import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';

import quotesApi from '../../../api/quotesApi';
import settingsApi from '../../../api/settingsApi';
import { getErrorMessage, getBlobErrorMessage } from '../../../utils/errorHandler';
import { printBlob, triggerBlobDownload } from '../../../utils/downloadFile';
import ProductImagePlaceholder from '../../../sections/client/products/ProductImagePlaceholder';
import QuantityStepper from '../../../sections/client/products/QuantityStepper';
import useAuth from '../../../auth/useAuth';
import { ROLES } from '../../../config';

const currencyPen = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });
const currencyUsd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

// Cada linea es un producto del catalogo ({ product, quantity }) o un producto "libre" que no
// esta registrado en el sistema ({ key, description, unit_price_pen, quantity }), con su precio
// en soles; el de dolares lo calcula el backend con el tipo de cambio (ver QuoteController::store).
const itemKey = (item) => (item.product ? `p-${item.product.id}` : item.key);

// Miniatura cuadrada reutilizada en la lista de busqueda y en "Cotizacion".
function ProductThumbnail({ product, size }) {
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0, borderRadius: 1, overflow: 'hidden' }}>
      {product.image_url ? (
        <Box
          component="img"
          src={product.image_url}
          alt={product.name}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', bgcolor: 'background.paper' }}
        />
      ) : (
        <ProductImagePlaceholder height="100%" iconFontSize={size / 2.5} />
      )}
    </Box>
  );
}

// Precio editable de un producto libre ya agregado. Guarda lo que se escribe en un estado local
// y solo lo aplica al salir del campo o con Enter; si queda vacio o negativo vuelve al anterior.
function CustomPriceInput({ value, onChange }) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = Number(draft);
    if (draft.trim() === '' || Number.isNaN(parsed) || parsed < 0) {
      setDraft(String(value));
      return;
    }
    const rounded = Math.round(parsed * 100) / 100;
    setDraft(String(rounded));
    if (rounded !== value) {
      onChange(rounded);
    }
  };

  return (
    <TextField
      size="small"
      type="number"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      sx={{ width: 130, mt: 0.5 }}
      slotProps={{
        htmlInput: { min: 0, step: '0.01', 'aria-label': 'Precio unitario en soles' },
        input: { startAdornment: <InputAdornment position="start">S/</InputAdornment> },
      }}
    />
  );
}

// Cotizador interno: misma herramienta de armado que "/cotizador" (busqueda, lista con
// cantidades, total en soles/dolares), pero para que administrador/tecnico arme y envie una
// cotizacion a nombre de un cliente que los contacta directamente (telefono, WhatsApp, etc.),
// en vez de que el cliente la arme por su cuenta.
export default function QuoteBuilderPage() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMINISTRADOR;

  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [items, setItems] = useState([]);
  const [customDescription, setCustomDescription] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  // Tipo de cambio para mostrar en dolares los productos libres; solo el administrador puede
  // consultarlo (ver ExchangeRateController), asi que para el tecnico queda en null.
  const [exchangeRate, setExchangeRate] = useState(null);

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedQuote, setSubmittedQuote] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [printingTicket, setPrintingTicket] = useState(false);

  // La lista de productos solo aparece cuando se escribe o se escanea algo en el buscador.
  useEffect(() => {
    const search = searchInput.trim();
    if (!search) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await quotesApi.searchCatalogProducts(search);
        if (!cancelled) {
          setSearchResults(data.data);
        }
      } catch (error) {
        // Busqueda secundaria: si falla no se interrumpe el armado de la cotizacion.
      } finally {
        if (!cancelled) {
          setSearching(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchInput]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    settingsApi
      .getExchangeRate()
      .then(({ data }) => setExchangeRate(Number(data.data.exchange_rate) || null))
      .catch(() => setExchangeRate(null));
  }, [isAdmin]);

  const addItem = (product) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.product?.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product?.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  // Enter en el buscador (la pistola lo manda al final de cada codigo): busca ya, sin esperar el
  // debounce, y agrega directo si hay un solo resultado; con varios, deja la lista para elegir.
  const handleSearchEnter = async (event) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    const code = searchInput.trim();
    if (!code) {
      return;
    }

    setSearching(true);
    try {
      const { data } = await quotesApi.searchCatalogProducts(code);
      if (data.data.length === 1) {
        addItem(data.data[0]);
        setSearchInput('');
      } else if (data.data.length === 0) {
        enqueueSnackbar(`No se encontró ningún producto con "${code}".`, { variant: 'warning' });
      } else {
        setSearchResults(data.data);
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo buscar el producto.'), { variant: 'error' });
    } finally {
      setSearching(false);
    }
  };

  const customPriceValue = Number(customPrice);
  const canAddCustom = customDescription.trim() !== '' && customPrice !== '' && customPriceValue >= 0;

  const addCustomItem = () => {
    if (!canAddCustom) {
      return;
    }
    setItems((prev) => [
      ...prev,
      { key: `c-${Date.now()}`, description: customDescription.trim(), unit_price_pen: customPriceValue, quantity: 1 },
    ]);
    setCustomDescription('');
    setCustomPrice('');
  };

  const handleCustomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomItem();
    }
  };

  const updateQuantity = (key, quantity) => {
    const parsed = Math.max(1, Number(quantity) || 1);
    setItems((prev) => prev.map((item) => (itemKey(item) === key ? { ...item, quantity: parsed } : item)));
  };

  // Solo los productos libres tienen precio editable; los del catalogo usan su precio registrado.
  const updateCustomPrice = (key, price) => {
    setItems((prev) =>
      prev.map((item) => (!item.product && itemKey(item) === key ? { ...item, unit_price_pen: price } : item))
    );
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((item) => itemKey(item) !== key));
  };

  const unitPricePen = (item) => (item.product ? item.product.price_pen : item.unit_price_pen);
  // Mismo redondeo que el backend: precio en soles / tipo de cambio.
  const unitPriceUsd = (item) =>
    item.product ? item.product.price_usd : exchangeRate ? Math.round((item.unit_price_pen / exchangeRate) * 100) / 100 : null;

  const hasCustomItems = items.some((item) => !item.product);
  const totalPen = items.reduce((sum, item) => sum + unitPricePen(item) * item.quantity, 0);
  // Sin tipo de cambio no se puede mostrar en dolares el total con productos libres.
  const totalUsd =
    hasCustomItems && !exchangeRate ? null : items.reduce((sum, item) => sum + unitPriceUsd(item) * item.quantity, 0);

  const resetForm = () => {
    setSubmittedQuote(null);
    setItems([]);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setCustomDescription('');
    setCustomPrice('');
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      enqueueSnackbar('Agrega al menos un producto a la cotización.', { variant: 'warning' });
      return;
    }
    if (!customerName.trim()) {
      enqueueSnackbar('Ingresa el nombre del cliente.', { variant: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const { data: response } = await quotesApi.createQuote({
        customer_name: customerName,
        customer_email: customerEmail.trim() || undefined,
        customer_phone: customerPhone || undefined,
        items: items.map((item) =>
          item.product
            ? { product_id: item.product.id, quantity: item.quantity }
            : { description: item.description, unit_price_pen: item.unit_price_pen, quantity: item.quantity }
        ),
      });
      setSubmittedQuote(response.data);
      setItems([]);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo registrar la cotización.'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Lleva los productos cotizados a la venta express (solo administrador), donde se eligen el
  // metodo de pago, la moneda y las series antes de registrar la venta.
  const handleRegisterSale = () => {
    navigate('/admin/venta-express', {
      state: { items, customerName },
    });
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await quotesApi.downloadQuotePdf(submittedQuote.id);
      triggerBlobDownload(response.data, `cotizacion-${submittedQuote.uuid}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo descargar el PDF.'), { variant: 'error' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Proforma en formato ticket de 80mm, directo a la tiketera (sin descargar el archivo).
  const handlePrintTicket = async () => {
    setPrintingTicket(true);
    try {
      const response = await quotesApi.downloadQuotePdf(submittedQuote.id, 'ticket');
      printBlob(response.data);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo imprimir la proforma.'), { variant: 'error' });
    } finally {
      setPrintingTicket(false);
    }
  };

  if (submittedQuote) {
    return (
      <Stack spacing={3}>
        <div>
          <Typography variant="h4">Cotizador</Typography>
          <Typography variant="body2" color="text.secondary">
            Arma una cotización con los productos del catálogo para enviarla a un cliente.
          </Typography>
        </div>

        <Card sx={{ maxWidth: 480 }}>
          <CardContent>
            <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h5">¡Cotización registrada!</Typography>
              <Typography color="text.secondary">
                Cotización N.° {submittedQuote.id} para {submittedQuote.customer_name}.
              </Typography>
              <Typography variant="h6">
                Total: {currencyPen.format(submittedQuote.subtotal_pen)} / {currencyUsd.format(submittedQuote.subtotal_usd)}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                >
                  {downloadingPdf ? 'Descargando...' : 'Descargar PDF'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PrintOutlinedIcon />}
                  onClick={handlePrintTicket}
                  disabled={printingTicket}
                >
                  {printingTicket ? 'Generando...' : 'Imprimir ticket'}
                </Button>
                <Button variant="contained" onClick={resetForm}>
                  Crear otra cotización
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4">Cotizador</Typography>
        <Typography variant="body2" color="text.secondary">
          Arma una cotización con productos del catálogo, o que no tengas registrados, para enviarla a un cliente
          que contacta directamente.
        </Typography>
      </div>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Producto no registrado
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Para cotizar algo que no tienes en el sistema (por ejemplo, un producto que traerás de un proveedor).
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Descripción"
                  size="small"
                  fullWidth
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  onKeyDown={handleCustomKeyDown}
                  slotProps={{ htmlInput: { maxLength: 255 } }}
                />
                <TextField
                  label="Precio (S/)"
                  size="small"
                  type="number"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  onKeyDown={handleCustomKeyDown}
                  sx={{ width: { sm: 160 }, flexShrink: 0 }}
                  slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addCustomItem}
                  disabled={!canAddCustom}
                  sx={{ flexShrink: 0 }}
                >
                  Agregar
                </Button>
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              <TextField
                fullWidth
                autoFocus
                label="Buscar producto"
                placeholder="Escribe el nombre o escanea el código y presiona Enter"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchEnter}
                slotProps={{
                  inputLabel: { shrink: true },
                  input: {
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
                  },
                }}
              />

              {!searching && searchInput.trim() && searchResults.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No encontramos productos con ese criterio.
                </Typography>
              )}

              <List>
                {searchResults.map((product) => (
                  <ListItem
                    key={product.id}
                    secondaryAction={
                      <IconButton edge="end" color="primary" onClick={() => addItem(product)}>
                        <AddIcon />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <ProductThumbnail product={product} size={48} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={product.name}
                      secondary={`${currencyPen.format(product.price_pen)} / ${currencyUsd.format(product.price_usd)}`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cotización
              </Typography>

              {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aún no agregaste productos.
                </Typography>
              ) : (
                <Stack divider={<Divider />} spacing={1.5}>
                  {items.map((item) => (
                    <Stack key={itemKey(item)} direction="row" alignItems="center" spacing={2}>
                      {item.product ? (
                        <ProductThumbnail product={item.product} size={56} />
                      ) : (
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            flexShrink: 0,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <EditNoteOutlinedIcon color="action" />
                        </Box>
                      )}
                      <Stack sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{item.product ? item.product.name : item.description}</Typography>
                        {item.product ? (
                          <Typography variant="caption" color="text.secondary">
                            {currencyPen.format(unitPricePen(item))}
                            {unitPriceUsd(item) !== null && ` / ${currencyUsd.format(unitPriceUsd(item))}`} c/u
                          </Typography>
                        ) : (
                          <>
                            <CustomPriceInput
                              value={item.unit_price_pen}
                              onChange={(price) => updateCustomPrice(itemKey(item), price)}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {unitPriceUsd(item) !== null && `${currencyUsd.format(unitPriceUsd(item))} · `}c/u · no
                              registrado
                            </Typography>
                          </>
                        )}
                      </Stack>
                      <QuantityStepper
                        value={item.quantity}
                        onChange={(quantity) => updateQuantity(itemKey(item), quantity)}
                      />
                      <IconButton onClick={() => removeItem(itemKey(item))} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}

              {items.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6">
                      {currencyPen.format(totalPen)}
                      {totalUsd !== null && ` / ${currencyUsd.format(totalUsd)}`}
                    </Typography>
                  </Stack>
                  {isAdmin && (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<ShoppingCartOutlinedIcon />}
                      onClick={handleRegisterSale}
                    >
                      Registrar venta
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Datos del cliente
              </Typography>
              <Stack spacing={2.5}>
                {items.length === 0 && (
                  <Alert severity="info">Agrega al menos un producto antes de enviar la cotización.</Alert>
                )}

                <TextField
                  label="Nombre completo"
                  fullWidth
                  required
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
                <TextField
                  label="Correo electrónico (opcional)"
                  type="email"
                  fullWidth
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                />
                <TextField
                  label="Teléfono (opcional)"
                  fullWidth
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                />

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={submitting || items.length === 0}
                >
                  {submitting ? 'Enviando...' : 'Enviar cotización'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Stack>
  );
}
